import { Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { BehaviorSubject, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { OrgLocation } from "../models/org-location.model";
import { Organization } from "../models/organization.model";
import * as utils from '../utils/utility-functions';
import { AngularFireFunctions } from "@angular/fire/compat/functions";

@Injectable({
    providedIn: 'root',
})

export class OrgCollectionService {

    private organiationLocation = new BehaviorSubject(null);
    currentOrganizationLocation = this.organiationLocation.asObservable();

    private organizationLocationList = new BehaviorSubject([]);
    currentOrganizationLocationList = this.organizationLocationList.asObservable();

    private organizationList = new BehaviorSubject([]);
    currentOrganizationList = this.organizationList.asObservable();

    private organizationLocationUserList = new BehaviorSubject([]);
    currentOrganizationLocationUserList = this.organizationLocationUserList.asObservable();

    private organiationLocations = new BehaviorSubject([]);
    currentOrganizationLocations = this.organiationLocations.asObservable();

    private organization = new BehaviorSubject(null);
    currentOrganization = this.organization.asObservable();

    constructor(private angularFirestore: AngularFirestore,
        private angularFunctions: AngularFireFunctions,
        private afAuth: AngularFireAuth) { }

    getOrgLocationAsync(orgLocationID: string) {
        return this.afAuth.authState.pipe(
            switchMap(user => {
                if (user) {
                    return this.angularFirestore.collection<OrgLocation>('OrgLocation')
                        .doc(orgLocationID)
                        .valueChanges({ idField: 'id' });
                } else {
                    return of(null);
                }
            })
        );
    }

    async getOrgLocation(orgLocationID: any) {
        try {
            let orgLocation = null;
            this.organiationLocation.next(null);

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocation');

            await collection.ref
                .doc(orgLocationID)
                .get()
                .then(querySnapshot => {
                    const docData = {
                        id: querySnapshot.id,
                        ...querySnapshot.data()
                    }
                    orgLocation = docData;
                }).catch(err => console.log("getOrgLocation query 1 error: " + err));

            this.updateOrgLocation(orgLocation);
            return orgLocation;
        } catch (error) {
            console.log("getOrgLocation query 1 error: " + error)
        }
    }

    async getAllLocations() {
        try {
            let orgLocations = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocation');

            await collection.ref
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        orgLocations.push(docData)
                    });
                }).catch(err => console.log("getAllLocations query 1 error: " + err));

            this.updateAllLocations(orgLocations);
            return orgLocations;
        } catch (error) {
            console.log(error);
        }
    }

    async getAllOrganizations() {
        try {
            let organizations = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Organizations');

            await collection.ref
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        organizations.push(docData)
                    });
                }).catch(err => console.log("getAllOrganizations query 1 error: " + err));

            this.updateAllOrganizations(organizations);
            return organizations;
        } catch (error) {
            console.log(error);
        }
    }

    async getOrgLocationById(orgLocationID: any) {
        try {
            let orgLocation = null;

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocation');

            await collection.ref
                .doc(orgLocationID)
                .get()
                .then(querySnapshot => {
                    const docData = {
                        id: querySnapshot.id,
                        ...querySnapshot.data()
                    }
                    orgLocation = docData;
                }).catch(err => console.log("getOrgLocationById query 1 error: " + err));

            return orgLocation;
        } catch (error) {
            console.log(error);
        }
    }

    async getOrgLocationByOrganizationID(orgID: any) {
        try {
            let orgLocation = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocation');

            await collection.ref
                .where('org_id', '==', orgID)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        orgLocation.push(docData);
                    });
                }).catch(err => console.log("getOrgLocationByOrganizationID query 1 error: " + err));

            return orgLocation[0];
        } catch (error) {
            console.log(error);
        }
    }

    async getOrgLocationUsersByLocationId(orgLocationID: any) {
        try {
            let orgLocationUsers = [];
            this.updateOrgLocationUserList(null);

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocationUser');

            await collection.ref
                .where('org_location_id', '==', orgLocationID)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        orgLocationUsers.push(docData)
                    });
                }).catch(err => console.log("getOrgLocationUsersByLocationId query 1 error: " + err));

            this.updateOrgLocationUserList(orgLocationUsers);
            return orgLocationUsers;
        } catch (error) {
            console.log(error);
        }
    }

    async removeLocationFromDoctor(orgLocationID: any, doctorID: string) {
        try {
            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocationUser');

            await collection.ref
                .where('org_location_id', '==', orgLocationID)
                .where('doctor_id', '==', doctorID)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        doc.ref.delete();
                    });
                }).catch(err => console.log("removeLocationFromDoctor query 1 error: " + err));

            return true;
        } catch (error) {
            console.log(error);
        }
    }

    async getOrgLocations(orgLocationID: any) {
        try {
            let orgLocations = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocation');

            await collection.ref
                .where('org_id', '==', orgLocationID)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach((data) => {
                        const docData = {
                            id: data.id,
                            ...data.data()
                        }
                        orgLocations.push(docData);
                    });
                }).catch(err => console.log("getOrgLocations query 1 error: " + err));

            this.updateOrgLocations(orgLocations);
            return orgLocations;
        } catch (error) {
            console.log(error);
        }
    }

    async getOrgLocationsByDoctorId(id: any) {
        try {
            let orgLocations = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocationUser');

            await collection.ref
                .where('doctor_id', '==', id)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach((data) => {
                        const docData = {
                            id: data.id,
                            ...data.data()
                        }
                        orgLocations.push(docData);
                    });
                }).catch(err => console.log("getOrgLocationsByDoctorId query 1 error: " + err));

            return orgLocations;
        } catch (error) {
            console.log(error);
        }
    }

    async getDoctorsByOrganizationID(id: string) {
        try {
            let doctors = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocationUser');

            await collection.ref
                .where('org_id', '==', id)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach((data) => {
                        const docData = {
                            id: data.id,
                            ...data.data()
                        }
                        doctors.push(docData);
                    });
                }).catch(err => console.log("getDoctorsByOrganizationID query 1 error: " + err));

            return doctors;
        } catch (error) {
            console.log(error);
        }
    }

    async updateOrgLocationAsync(orgLocation: OrgLocation) {
        try {
            let { id, ...orgLocationToUpdate } = orgLocation;

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocation');

            await collection.ref
                .doc(id)
                .update(JSON.parse(JSON.stringify(orgLocationToUpdate)))
                .catch(err => console.log("updateOrgLocationAsync query 1 error: " + err));

            this.updateOrgLocation(orgLocation);

            return orgLocation;
        } catch (error) {
            console.log(error);
        }
    }

    async saveNewOrgLocation(orgLocation: OrgLocation) {
        try {
            let orgLocationID = null;

            const organizationLocationCollection: AngularFirestoreCollection = this.angularFirestore.collection('OrgLocation');

            const newOrgLocation = utils.createNewOrgLocationObject(orgLocation);

            await organizationLocationCollection.ref
                .add(JSON.parse(JSON.stringify(newOrgLocation)))
                .then(function (docRef) {
                    orgLocationID = docRef.id;
                })
                .catch(err => console.log("saveNewOrgLocation query 1 error: " + err));

            const callable = this.angularFunctions.httpsCallable('adminCreateNewLocation');

            callable({
                orgLocationID: orgLocationID,
            })
                .toPromise()
                .then((result) => {
                    console.log('adminCreateNewLocation', result);
                })
                .catch((error) => console.log('adminCreateNewLocation', error));

            return newOrgLocation;
        } catch (error) {
            console.log(error);
        }
    }

    async saveNewOrganization(organization: Organization) {
        try {
            const organizationLocationCollection: AngularFirestoreCollection = this.angularFirestore.collection('Organizations');

            const newOrganization = utils.createOrganizationObject(organization);

            await organizationLocationCollection.ref
                .add(JSON.parse(JSON.stringify(newOrganization)))
                .catch(err => console.log("saveNewOrganization query 1 error: " + err));

            return newOrganization;
        } catch (error) {
            console.log(error);
        }
    }

    async getOrganization(organizationID: any) {
        try {
            let organization = null;

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Organizations');

            await collection.ref
                .doc(organizationID)
                .get()
                .then(querySnapshot => {
                    const docData = {
                        id: querySnapshot.id,
                        ...querySnapshot.data()
                    }
                    organization = docData;
                }).catch(err => console.log("getOrganization query 1 error: " + err));

            this.updateOrganization(organization);
            return organization;
        } catch (error) {
            console.log(error);
        }
    }


    updateOrganization(organization) {
        let org = utils.createOrganization(organization);
        this.organization.next(org);
    }

    updateOrgLocations(locations) {
        let orgLocationListTemp = [];
        locations.forEach(location => {
            let orgLocation = utils.createOrgLocation(location);
            orgLocationListTemp.push(orgLocation);
        });

        this.organiationLocations.next(orgLocationListTemp);
    }

    updateOrgLocationUserList(users: any) {
        this.organizationLocationUserList.next(users);
    }

    updateAllOrganizations(organizations: any[]) {
        let organizationsTemp: Organization[] = [];
        organizations.forEach(organization => {
            let organizationTemp = utils.createOrganization(organization);
            organizationsTemp.push(organizationTemp);
        });
        this.organizationList.next(organizationsTemp);
    }

    updateAllLocations(orgLocations: any[]) {
        let locations: OrgLocation[] = [];
        orgLocations.forEach(location => {
            let locationTemp = utils.createOrgLocation(location);
            locations.push(locationTemp);
        });
        this.organizationLocationList.next(locations);
    }

    updateOrgLocation(location) {
        let orgLocation = utils.createOrgLocation(location);
        this.organiationLocation.next(orgLocation);
    }
}